import { useMemo, useState } from 'react'
import { isPresent } from 'utils'
import {
  activate as activateLandingURL,
  fetchBizChannelSiteSetting,
} from 'features/dashboard/operation/keywordLandingUrlEdit/keywordLandingUrlEditSlice'
import SelectBox from 'components/form/SelectBox'
import {
  KeywordTableEditTypes,
  setEditType,
  setSelectedIds,
} from 'features/dashboard/table/keyword/keywordTableSlice'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import { CampaignTypes, ReviewStatus } from 'features/Codes'
import { useAlert } from 'features/dialog/Alert'
import { hideBackDrop, showBackDrop } from 'features/dialog/backDropSlice'
import { showKeywordLandingUrlEdit } from 'features/dashboard/operation/keywordLandingUrlEdit/keywordLandingUrlEditModalSlice'
import api from 'api/axios/foka'
import { fail } from '../../../toast/Toast'

const KeywordLandingUrlEditSelect = () => {
  const dispatch = useDispatch()
  const {
    keywordTable: { content, selectedIds },
    keywordLandingURL: { siteSettingMap },
  } = useSelector(
    ({ dashboard }) => ({
      keywordTable: dashboard.table.keywordTable,
      keywordLandingURL: dashboard.operation.keywordLandingURL,
    }),
    shallowEqual
  )
  const disabled = useMemo(
    () =>
      selectedIds.length === 0 ||
      content
        .filter(({ id }) => selectedIds.includes(id))
        .every(({ keywordType }) => keywordType === CampaignTypes.TALK_CHANNEL),
    [selectedIds, content]
  )
  const selectedPremiumLinkKeywords = useMemo(
    () =>
      content.filter(
        ({ id, keywordType }) =>
          selectedIds.includes(id) && keywordType === CampaignTypes.PREMIUM_LINK
      ),
    [content, selectedIds]
  )
  const [hasTalkChannel, setHasTalkChannel] = useState(false)
  const bizChannelId = useMemo(
    () => selectedPremiumLinkKeywords[0]?.parentDomainInfo?.bizChannel?.id,
    [selectedPremiumLinkKeywords]
  )
  const landingUrlEditableSelectedKeywordIds = useMemo(
    () =>
      selectedPremiumLinkKeywords
        .filter(
          ({ landingUrlReviewStatus, landingUrl }) =>
            !isPresent(landingUrl) ||
            ReviewStatus[landingUrlReviewStatus] !== ReviewStatus.APPROVING
        )
        .map(({ id }) => id),
    [selectedPremiumLinkKeywords]
  )
  const landingUrls = useMemo(
    () =>
      selectedPremiumLinkKeywords.map(({ id, text, landingUrl }) => ({
        id,
        text,
        landingUrl,
      })),
    [selectedPremiumLinkKeywords]
  )

  const [NonEditableAlert, showNonEditableAlert] = useAlert()
  const [NonEditableFilteredBulkAlert, showNonEditableFilteredBulkAlert] =
    useAlert()
  const [
    NonEditableFilteredIndividualAlert,
    showNonEditableFilteredIndividualAlert,
  ] = useAlert()
  const [BizChannelAlert, showBizChannelAlert] = useAlert()

  const checkSameSiteSetting = async () => {
    if (selectedPremiumLinkKeywords.length === 1) {
      return { hasSameSiteSetting: true }
    }
    dispatch(showBackDrop())
    const { payload: siteSettings, error } = await dispatch(
      fetchBizChannelSiteSetting()
    )
    dispatch(hideBackDrop())
    if (error) {
      return { error }
    } else {
      const hasSameSiteSetting =
        selectedPremiumLinkKeywords.length > 1 &&
        selectedPremiumLinkKeywords
          .map(
            ({
              parentDomainInfo: {
                bizChannel: { id },
              },
            }) => id
          )
          .every((value, index, array) => {
            const currentSiteSetting = siteSettings[value]
            const firstSiteSetting = siteSettings[array[0]]
            return (
              currentSiteSetting.hasPcSite === firstSiteSetting.hasPcSite &&
              currentSiteSetting.hasMobileSite ===
                firstSiteSetting.hasMobileSite &&
              currentSiteSetting.isIndividual === firstSiteSetting.isIndividual
            )
          })
      return {
        hasSameSiteSetting,
        siteSettings,
      }
    }
  }

  const getSiteSetting = async siteSettings => {
    if (selectedPremiumLinkKeywords.length === 1) {
      const {
        data: {
          siteUrl: { pcSiteYn, mobileSiteYn, individualSettingYn },
        },
      } = await api.ad.getBizChannel(bizChannelId)
      return {
        hasPcSite: pcSiteYn === 'Y',
        hasMobileSite: mobileSiteYn === 'Y',
        isIndividual: individualSettingYn === 'Y',
      }
    } else {
      const map = siteSettings || siteSettingMap
      return map[bizChannelId]
    }
  }

  const handleModifyLandingURL = async mode => {
    setHasTalkChannel(selectedIds.length !== selectedPremiumLinkKeywords.length)
    if (landingUrlEditableSelectedKeywordIds.length === 0) {
      return showNonEditableAlert()
    }
    const { hasSameSiteSetting, siteSettings, error } =
      await checkSameSiteSetting()
    if (error) {
      fail('알 수 없는 에러가 발생했습니다')
      return
    }
    if (!hasSameSiteSetting) {
      return showBizChannelAlert()
    }
    const isAllEditable =
      landingUrlEditableSelectedKeywordIds.length ===
      selectedPremiumLinkKeywords.length
    if (!isAllEditable) {
      dispatch(setSelectedIds(landingUrlEditableSelectedKeywordIds))
    }
    const siteSetting = await getSiteSetting(siteSettings)
    switch (mode) {
      case '일괄변경':
        isAllEditable
          ? dispatch(
              showKeywordLandingUrlEdit({
                keywords: landingUrls,
                siteSetting,
              })
            )
          : showNonEditableFilteredBulkAlert()
        break
      case '개별변경':
        isAllEditable
          ? batch(() => {
              dispatch(
                activateLandingURL({
                  ids: selectedPremiumLinkKeywords.map(({ id }) => id),
                  siteSetting,
                })
              )
              dispatch(setEditType(KeywordTableEditTypes.landingUrl))
            })
          : showNonEditableFilteredIndividualAlert()
        break
      default:
    }
  }
  return (
    <>
      <SelectBox
        noBlurOnDefault
        placeholder="랜딩URL 변경"
        value
        disabled={disabled}
        onValueChange={handleModifyLandingURL}>
        <SelectBox.Item tiaraAction="키워드탭_랜딩URL_일괄_클릭">
          일괄변경
        </SelectBox.Item>
        <SelectBox.Item tiaraAction="키워드탭_랜딩URL_개별_클릭">
          개별변경
        </SelectBox.Item>
      </SelectBox>
      <NonEditableAlert title="랜딩URL변경">
        {hasTalkChannel ? (
          <>
            (랜딩URL 변경이 불가한 톡채널검색 유형의 전체 키워드를 제외하고)
            선택된 키워드의 랜딩URL이 현재 {ReviewStatus.APPROVING.title}으로
            변경이 불가능합니다.
          </>
        ) : (
          <>
            선택된 키워드의 랜딩URL이 현재 {ReviewStatus.APPROVING.title}으로
            변경이 불가능합니다.
          </>
        )}
      </NonEditableAlert>
      <NonEditableFilteredBulkAlert
        title="랜딩URL변경"
        onClose={async () => {
          if (!disabled) {
            const siteSetting = await getSiteSetting()
            dispatch(
              showKeywordLandingUrlEdit({
                keywords: landingUrls,
                siteSetting,
              })
            )
          }
        }}>
        {hasTalkChannel ? (
          <>
            (랜딩URL 변경이 불가한 톡채널검색 유형의 전체 키워드를 제외하고)
            일부 키워드의 랜딩URL이 현재 {ReviewStatus.APPROVING.title}으로
            변경이 불가능하여 선택에서 제외되었습니다.
          </>
        ) : (
          <>
            일부 키워드의 랜딩URL이 현재 {ReviewStatus.APPROVING.title}으로
            변경이 불가능하여 선택에서 제외되었습니다.
          </>
        )}
      </NonEditableFilteredBulkAlert>
      <NonEditableFilteredIndividualAlert
        title="랜딩URL변경"
        onClose={() => {
          if (!disabled) {
            batch(async () => {
              const siteSetting = await getSiteSetting()
              dispatch(
                activateLandingURL({
                  ids: selectedIds,
                  siteSetting,
                })
              )
              dispatch(setEditType(KeywordTableEditTypes.landingUrl))
            })
          }
        }}>
        일부 키워드의 랜딩URL이 현재 {ReviewStatus.APPROVING.title}으로 변경이
        불가능하여 선택에서 제외되었습니다.
      </NonEditableFilteredIndividualAlert>
      <BizChannelAlert title="비즈채널 URL 설정 미일치">
        키워드가 소속된 비즈채널의 URL 설정
        <br />
        (모바일/PC 사용 여부)값이 같은 키워드 단위로 변경 가능합니다.
      </BizChannelAlert>
    </>
  )
}
export default KeywordLandingUrlEditSelect
